import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { mapUserRoles } from "../../../../functions/useRoleMapping";
import useHandleBlur from "../../../../functions/useHandleBlur";
import { useFormState } from "../../../../functions/useFormState";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../../redux/slices/apiUrlSlice";
import { selectUv } from "../../../../redux/selectors";
import { selectApiStatus } from "../../../../redux/slices/apiStatusSlice";
import { selectFieldDescriptions } from "../../../../redux/selectors";
import useFieldClass from "../../../../functions/useFieldClass";

const UV = ({ zone }) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;
  const sectionName = "uvSystem";

  const apiUrls = useSelector(selectApiUrls);
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const error = null;

  const storedJobDetails = useSelector(selectUv);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const apiStatus = useSelector(selectApiStatus);
  const loading = apiStatus[sectionName] !== "success";

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});
  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        model: jobDetails.Model || "",
        quantity: jobDetails.Quantity || "",
        comments: jobDetails.Comments || "",
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly);
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly;

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const fieldClasses = useFieldClass(formData, zone);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <Row className="mb-3 filtration-field-row">
            <Col>
              <Form.Group as={Row} controlId="model">
                <Form.Label column sm={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Model")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Model</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="model"
                    value={formData.model || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Model", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "Model",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.model}
                    onChange={handleChange}
                    placeholder="Enter Model"
                  />
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="quantity">
                <Form.Label column sm={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Quantity")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Quantity</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="quantity"
                    value={formData.quantity || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Quantity", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "Quantity",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.quantity}
                    onChange={handleChange}
                    placeholder="Enter Quantity"
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3 filtration-field-row">
            <Col>
              <Form.Group controlId="comments">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Comments")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Comments</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="comments"
                  value={formData.comments || ""}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(e, "Comments", [], {
                      ID: formData.ID,
                      url: apiUrl,
                      field: "Comments",
                      tableKey,
                    })
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.comments}
                  onChange={handleChange}
                  placeholder="Enter Comments"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default UV;
